import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bookmark, Share2, Search, Plus, MessageCircle, FolderOpen, ArrowRight, X, ChevronLeft, ChevronRight, Check, Send, Heart, Repeat2, Film, FileText, Headphones, Utensils, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { FLAGS } from '../lib/flags';
import { track } from '../lib/analytics';

const ONBOARDING_KEY = 'bs_onboarding_complete';

// ── Welcome Slides ──
// Slide #2 is intentionally NOT a static icon/text slide - it has a
// custom animated phone-mockup component (`ShareToBrainSyncDemo`)
// rendered in place of the usual hero icon. The share-to-BrainSync
// flow is the highest-leverage feature in the app (one tap saves
// from any other app via the OS share sheet), so it gets a real
// motion demo instead of just words.
//
// Both slides use a dark forest gradient (`emerald-950` → `zinc-950`)
// that matches BrainSync's primary brand colour `#2E3A2F` so the
// onboarding feels like a part of the product, not a generic AI-app
// purple gradient.
const SLIDES = [
    {
        icon: Bookmark,
        kind: 'welcome-orbit',
        title: 'Welcome to BrainSync',
        subtitle: 'Your AI-powered second brain',
        description: 'Save any link - articles, videos, recipes, podcasts - and BrainSync will summarize, categorize, and make it searchable instantly.',
        color: 'from-emerald-700 via-emerald-950 to-zinc-950',
    },
    {
        icon: Share2,
        kind: 'share-demo',
        title: 'Share from any app',
        subtitle: 'The fastest way to save',
        description: "Found a great post on TikTok, Instagram, YouTube, or X? Tap the share button, pick BrainSync, and it's saved - title, transcript, summary, and tags handled automatically.",
        color: 'from-zinc-900 via-emerald-950 to-zinc-900',
    },
];

// ── Tooltip Steps (reference data-testid targets) ──
const TOOLTIP_STEPS = [
    {
        target: 'add-bookmark-btn',
        title: 'Add a Bookmark',
        description: 'Tap here to save a URL, write a note, or use voice input.',
        position: 'bottom',
    },
    {
        target: 'search-input',
        title: 'AI-Powered Search',
        description: 'Search across all your bookmarks. AI search understands meaning, not just keywords.',
        position: 'bottom',
    },
    {
        target: 'categories-bar',
        title: 'Collections',
        description: 'Organize bookmarks into custom collections. BrainSync auto-suggests collections too.',
        position: 'bottom',
    },
    {
        target: 'ai-chat-bubble',
        title: 'Ask BrainSync',
        description: 'Chat with your bookmarks. Ask questions and get answers from your saved content.',
        position: 'top-left',
    },
];

// ── BrainSyncOrbit ──
// Hero graphic for slide #1 (Welcome). Visually conveys the product's
// core promise in a single glance: the BrainSync brand mark sits at
// the centre, with six content-type orbs (Videos, Articles, Podcasts,
// Recipes, Links, AI) slowly orbiting around it. The brain is also
// pulsing - concentric emerald rings expand outward like a beacon -
// so the whole thing feels like a "live, thinking" second-brain
// rather than a static logo.
//
// Implementation notes:
//   • The whole ring rotates with `bs-orbit-rotate` (30s/revolution),
//     while each orb internally counter-rotates at the same speed so
//     the lucide icon stays upright instead of tumbling end-over-end.
//   • Each orb has a staggered `bs-orb-glow` (scale + opacity pulse)
//     with `animation-delay` offset 0.4s per orb - produces a
//     sequential "lighting up clockwise" feel without a switch
//     statement.
//   • Two faint concentric ring guides (`border-emerald-400/15`)
//     hint at the orbit path and add depth.
//   • Pure CSS, no external assets beyond `/icon-192.png` (already
//     bundled, also reused by the share demo).
export function BrainSyncOrbit() {
    const orbs = [
        { icon: Film, color: 'bg-rose-500' },
        { icon: FileText, color: 'bg-blue-500' },
        { icon: Headphones, color: 'bg-purple-500' },
        { icon: Utensils, color: 'bg-orange-500' },
        { icon: Bookmark, color: 'bg-cyan-500' },
        { icon: Sparkles, color: 'bg-emerald-500' },
    ];
    const radius = 86;
    return (
        <div className="relative w-[220px] h-[220px] mb-3" data-testid="brain-orbit">
            <style>{`
                @keyframes bs-orbit-rotate {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes bs-orbit-counter {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(-360deg); }
                }
                @keyframes bs-brain-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45), 0 0 28px 6px rgba(16,185,129,0.25); }
                    50% { box-shadow: 0 0 0 16px rgba(16,185,129,0), 0 0 40px 12px rgba(16,185,129,0.35); }
                }
                @keyframes bs-orb-glow {
                    0%, 100% { transform: scale(0.92); filter: brightness(0.85); }
                    50% { transform: scale(1.12); filter: brightness(1.15); }
                }
                .bs-orbit-ring { animation: bs-orbit-rotate 30s linear infinite; }
                .bs-orbit-counter { animation: bs-orbit-counter 30s linear infinite; }
                .bs-brain-pulse { animation: bs-brain-pulse 3.2s ease-in-out infinite; }
                .bs-orb-glow { animation: bs-orb-glow 2.8s ease-in-out infinite; }
            `}</style>

            {/* Concentric orbit guides - barely there, hint at a
                "system diagram" without becoming a chart. */}
            <div className="absolute inset-0 rounded-full border border-emerald-400/15" />
            <div className="absolute inset-[18px] rounded-full border border-emerald-400/10" />

            {/* Rotating orbs container */}
            <div className="absolute inset-0 bs-orbit-ring">
                {orbs.map((orb, i) => {
                    const angle = (i * 360) / orbs.length - 90;
                    const x = Math.cos((angle * Math.PI) / 180) * radius;
                    const y = Math.sin((angle * Math.PI) / 180) * radius;
                    const Icon = orb.icon;
                    return (
                        <div
                            key={i}
                            className="absolute top-1/2 left-1/2"
                            style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                        >
                            {/* Counter-rotate so the icon stays upright as
                                the ring spins. */}
                            <div className="bs-orbit-counter">
                                <div
                                    className={`bs-orb-glow w-11 h-11 rounded-2xl ${orb.color} shadow-[0_8px_20px_-4px_rgba(0,0,0,0.4)] ring-2 ring-white/25 flex items-center justify-center`}
                                    style={{ animationDelay: `${i * 0.4}s` }}
                                >
                                    <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Central brand mark - the actual app icon with a soft
                emerald aura that pulses outward. */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="bs-brain-pulse w-[84px] h-[84px] rounded-[22px] overflow-hidden ring-2 ring-white/25 shadow-2xl bg-[#1B2632]">
                    <img
                        src="/icon-192.png"
                        alt="BrainSync"
                        className="w-full h-full"
                        loading="eager"
                    />
                </div>
            </div>
        </div>
    );
}

// ── Share-to-BrainSync animated demo ──
// Exported for reuse: also rendered by `ShareDemoRefresherModal`
// (Dashboard user-dropdown → "How to share from any app") so existing
// users who skipped onboarding can still discover this workflow.
//
// Pure-CSS keyframed mini-phone mockup. Demos the real Instagram/
// Reels/TikTok flow as captured by the user in screenshots - most
// onboardings get this wrong by jumping straight from "tap share" to
// "system share sheet", which skips the in-app share sheet that
// actually shows up first in IG/FB. We model all three stages:
//
//   t=0.0s  - Reels-style post, right-side action column (♥ 💬 🔁 ✈ 🔖)
//   t=1.0s  - paper-plane Send icon glows + receives a tap ring
//   t=2.0s  - IG/FB-style "in-app share sheet" rises: friends grid +
//             bottom apps row, the "More" / "Share" target glows
//   t=3.6s  - that sheet drops, the Android system share sheet rises
//   t=4.8s  - BrainSync row inside the system sheet flashes
//             (with the actual brand mark - dark-green square +
//             Brain glyph - so users recognise it on their phone)
//   t=6.0s  - "✓ Saved to BrainSync" toast slides down from the top
//   t=8.0s  - loop restarts
//
// A small step pill above the phone labels the current phase
// ("1. Tap share button" / "2. Tap More" / "3. Pick BrainSync") so
// the visual flow is unambiguous. The whole thing is pure CSS - no
// external assets, no JS state machine, the keyframe percentages
// hand-off cleanly across an 8-second loop.
export function ShareToBrainSyncDemo() {
    return (
        <div
            className="relative w-full flex flex-col items-center justify-center py-2"
            data-testid="share-demo-mockup"
            data-active="true"
        >
            <style>{`
                /* 8-second master timeline. Percentages below are all
                   relative to 8s. */
                @keyframes bs-share-icon-pulse {
                    0%, 8% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
                    14% { transform: scale(1.22); box-shadow: 0 0 0 12px rgba(255,255,255,0); }
                    22%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0); }
                }
                @keyframes bs-app-sheet-rise {
                    0%, 22% { transform: translateY(100%); opacity: 0; }
                    27%, 42% { transform: translateY(0); opacity: 1; }
                    47%, 100% { transform: translateY(100%); opacity: 0; }
                }
                @keyframes bs-app-more-flash {
                    0%, 32% { background-color: rgba(255,255,255,0); transform: scale(1); }
                    37%, 42% { background-color: rgba(16,185,129,0.18); transform: scale(1.08); }
                    47%, 100% { background-color: rgba(255,255,255,0); transform: scale(1); }
                }
                @keyframes bs-sys-sheet-rise {
                    0%, 47% { transform: translateY(100%); opacity: 0; }
                    52%, 75% { transform: translateY(0); opacity: 1; }
                    80%, 100% { transform: translateY(100%); opacity: 0; }
                }
                @keyframes bs-target-flash {
                    0%, 60% { background-color: rgba(255,255,255,0); }
                    65%, 72% { background-color: rgba(16,185,129,0.20); }
                    77%, 100% { background-color: rgba(255,255,255,0); }
                }
                @keyframes bs-saved-toast {
                    0%, 78% { transform: translate(-50%, -160%); opacity: 0; }
                    82%, 94% { transform: translate(-50%, 0); opacity: 1; }
                    98%, 100% { transform: translate(-50%, -160%); opacity: 0; }
                }
                @keyframes bs-post-fade {
                    0%, 20% { opacity: 1; }
                    25%, 100% { opacity: 0.55; }
                }
                /* Step banner - slides in from the top of the phone
                   screen and out again. Slight stagger between phases
                   so the next banner is already easing in as the
                   previous one fades out. */
                @keyframes bs-banner-1 {
                    0%, 2% { transform: translateY(-150%); opacity: 0; }
                    6%, 22% { transform: translateY(0); opacity: 1; }
                    26%, 100% { transform: translateY(-150%); opacity: 0; }
                }
                @keyframes bs-banner-2 {
                    0%, 26% { transform: translateY(-150%); opacity: 0; }
                    30%, 47% { transform: translateY(0); opacity: 1; }
                    51%, 100% { transform: translateY(-150%); opacity: 0; }
                }
                @keyframes bs-banner-3 {
                    0%, 51% { transform: translateY(-150%); opacity: 0; }
                    55%, 76% { transform: translateY(0); opacity: 1; }
                    80%, 100% { transform: translateY(-150%); opacity: 0; }
                }
                @keyframes bs-banner-4 {
                    0%, 78% { transform: translateY(-150%); opacity: 0; }
                    82%, 96% { transform: translateY(0); opacity: 1; }
                    98%, 100% { transform: translateY(-150%); opacity: 0; }
                }
                .bs-anim { animation-duration: 12s; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
                .bs-share-icon { animation-name: bs-share-icon-pulse; }
                .bs-app-sheet { animation-name: bs-app-sheet-rise; }
                .bs-app-more { animation-name: bs-app-more-flash; }
                .bs-sys-sheet { animation-name: bs-sys-sheet-rise; }
                .bs-target { animation-name: bs-target-flash; }
                .bs-toast { animation-name: bs-saved-toast; }
                .bs-post { animation-name: bs-post-fade; }
                .bs-banner-1 { animation-name: bs-banner-1; }
                .bs-banner-2 { animation-name: bs-banner-2; }
                .bs-banner-3 { animation-name: bs-banner-3; }
                .bs-banner-4 { animation-name: bs-banner-4; }
            `}</style>

            {/* Soft emerald glow behind the phone - radial wash that
                makes the device feel premium and floating, rather
                than just sitting flat on a gradient. Sized larger
                than the phone so it bleeds out on all sides. */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[320px] h-[320px] rounded-full bg-emerald-400/25 blur-3xl" />
            </div>

            {/* Phone frame - premium hardware look:
                - Outer gunmetal bezel (`from-zinc-700 to-zinc-900`)
                - Inner deep-black screen
                - Long ambient shadow + tight rim shadow
                - Subtle inner highlight along the top edge
                - Modern Dynamic-Island-style pill instead of an
                  old-style notch
            */}
            <div className="relative w-[228px] h-[400px] rounded-[36px] bg-gradient-to-b from-zinc-700 to-zinc-900 p-[3px] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.55),0_15px_30px_-10px_rgba(0,0,0,0.4)]">
                <div className="relative w-full h-full rounded-[33px] bg-black overflow-hidden">
                    {/* Top edge highlight - sells the metallic bezel */}
                    <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent z-40" />
                    {/* Status bar */}
                    <div className="absolute top-2 left-0 right-0 h-3 flex items-center justify-between px-4 text-[8px] text-white/80 font-semibold z-20">
                        <span>9:41</span>
                        <span className="flex items-center gap-0.5">
                            <span className="w-1 h-1 rounded-full bg-white/80" />
                            <span className="w-1 h-1 rounded-full bg-white/80" />
                            <span className="w-1 h-1 rounded-full bg-white/80" />
                        </span>
                    </div>
                    {/* Dynamic Island pill */}
                    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-30 ring-1 ring-zinc-950/50" />

                    {/* Step banner - sits inside the phone, dead-center
                        on the screen. Acts as an in-product tutorial
                        chip so the user reads it in context, not
                        above the device. Glass treatment
                        (white/95 + backdrop-blur) with a heavy shadow
                        so it stays legible over any underlying phase
                        (reels post, app share sheet, system share
                        sheet). Sits above everything else via z-50. */}
                    <div className="absolute top-1/2 -translate-y-1/2 left-3 right-3 z-50 h-10 pointer-events-none">
                        <StepBanner className="bs-anim bs-banner-1" n={1} label="Tap the share button" tone="emerald" />
                        <StepBanner className="bs-anim bs-banner-2" n={2} label="Then tap More" tone="emerald" />
                        <StepBanner className="bs-anim bs-banner-3" n={3} label="Pick BrainSync" tone="emerald" />
                        <StepBanner className="bs-anim bs-banner-4" n="✓" label="Saved to BrainSync" tone="success" />
                    </div>

                    {/* Reels-style post - full-bleed image + right-side
                        action column. Modelled directly off the IG/Reels
                        screenshot the user shared. */}
                    <div className="absolute inset-0 pt-5 bs-anim bs-post">
                        {/* Background "video" - a moody gradient stands in
                            for the actual reel content. */}
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-rose-700">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(255,255,255,0.25),transparent_55%)]" />
                        </div>

                        {/* Right-side action column - the share icon
                            position the user circled in red on the
                            screenshot. */}
                        <div className="absolute right-1.5 bottom-12 flex flex-col items-center gap-2.5 z-10">
                            <div className="flex flex-col items-center">
                                <Heart className="w-4 h-4 text-white drop-shadow" strokeWidth={2.5} />
                                <span className="text-[6px] text-white font-semibold drop-shadow">197K</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <MessageCircle className="w-4 h-4 text-white drop-shadow" strokeWidth={2.5} />
                                <span className="text-[6px] text-white font-semibold drop-shadow">681</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <Repeat2 className="w-4 h-4 text-white drop-shadow" strokeWidth={2.5} />
                                <span className="text-[6px] text-white font-semibold drop-shadow">4.3K</span>
                            </div>
                            {/* THE share icon - paper-plane, glowing.
                                Wrapped in a circle so the box-shadow pulse
                                ring from `bs-share-icon` reads cleanly. */}
                            <div className="flex flex-col items-center">
                                <div className="bs-anim bs-share-icon flex items-center justify-center w-7 h-7 rounded-full">
                                    <Send className="w-4 h-4 text-white drop-shadow" strokeWidth={2.5} />
                                </div>
                                <span className="text-[6px] text-white font-semibold drop-shadow">113K</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <Bookmark className="w-4 h-4 text-white drop-shadow" strokeWidth={2.5} />
                                <span className="text-[6px] text-white font-semibold drop-shadow">28K</span>
                            </div>
                        </div>

                        {/* Creator handle (bottom-left) */}
                        <div className="absolute left-2 bottom-3 right-12 text-white">
                            <div className="flex items-center gap-1.5">
                                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-pink-500 to-orange-400 ring-1 ring-white/60" />
                                <div className="text-[8px] font-semibold drop-shadow">@creator</div>
                            </div>
                        </div>
                    </div>

                {/* Stage 2 - the social app's OWN share sheet (modelled
                    off the IG/FB screenshots: friends grid on top,
                    bottom app row including a "More/Share" target
                    that opens the system sheet). */}
                <div className="bs-anim bs-app-sheet absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl pt-1.5 pb-2 px-2 z-10">
                    <div className="w-8 h-1 bg-zinc-300 rounded-full mx-auto mb-1.5" />
                    {/* Friends grid */}
                    <div className="grid grid-cols-4 gap-1 mb-1.5">
                        {['#f59e0b', '#a855f7', '#10b981', '#3b82f6'].map((bg, i) => (
                            <div key={i} className="flex flex-col items-center gap-0.5">
                                <div className="w-7 h-7 rounded-full" style={{ background: bg }} />
                                <div className="text-[5px] text-zinc-700 font-medium leading-tight text-center">Friend</div>
                            </div>
                        ))}
                    </div>
                    {/* Bottom app row - the "More" target highlights
                        because that's what opens the system sheet. */}
                    <div className="flex items-center gap-2 pt-1.5 border-t border-zinc-100">
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-[8px] font-bold">W</div>
                            <div className="text-[5px] text-zinc-600">WhatsApp</div>
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-700 text-[10px]">🔗</div>
                            <div className="text-[5px] text-zinc-600">Copy link</div>
                        </div>
                        {/* Highlighted "More / Share" - this is the
                            button the user circled in red. */}
                        <div className="bs-anim bs-app-more flex flex-col items-center gap-0.5 rounded-md px-1 py-0.5">
                            <div className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center ring-2 ring-emerald-400 ring-offset-1 ring-offset-white">
                                <Share2 className="w-3 h-3 text-zinc-800" strokeWidth={2.5} />
                            </div>
                            <div className="text-[5px] font-bold text-emerald-700">More</div>
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-700 text-[10px]">+</div>
                            <div className="text-[5px] text-zinc-600">Story</div>
                        </div>
                    </div>
                </div>

                {/* Stage 3 - Android system share sheet. Drives the
                    point home: this is the sheet where BrainSync
                    actually shows up. The brand mark is rendered with
                    the real app icon style: dark-green square + Brain
                    glyph (matches /favicon and the TWA launcher). */}
                <div className="bs-anim bs-sys-sheet absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl pt-1.5 pb-2 px-2 z-10">
                    <div className="w-8 h-1 bg-zinc-300 rounded-full mx-auto mb-1.5" />
                    <div className="text-[6px] text-zinc-500 font-semibold uppercase tracking-wide px-1 mb-0.5">Sharing link</div>
                    <div className="text-[6px] text-zinc-400 bg-zinc-50 rounded px-1.5 py-1 mb-1.5 truncate">
                        instagram.com/reel/DV3uh4oDxc7
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                        {/* WhatsApp */}
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-7 h-7 rounded-xl bg-green-500 flex items-center justify-center text-white text-[10px] font-bold">W</div>
                            <div className="text-[5px] text-zinc-700 leading-tight">WhatsApp</div>
                        </div>
                        {/* Quick Share */}
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-7 h-7 rounded-xl bg-blue-500 flex items-center justify-center text-white text-[9px]">⇄</div>
                            <div className="text-[5px] text-zinc-700 leading-tight">Quick</div>
                        </div>
                        {/* BrainSync - the highlighted target. Uses
                            the REAL app icon PNG (`/icon-192.png`)
                            instead of a hand-rolled lucide approximation
                            so the demo matches exactly what users will
                            see on their own phone (dark-navy square +
                            stylised green brain glyph, sitting inside
                            the share-sheet's white circle backdrop -
                            same as the screenshot the user shared).
                            The flash overlay (`.bs-target`) sits on
                            the column wrapper so the highlight feels
                            like a tap-pulse, not a static ring. */}
                        <div className="bs-anim bs-target relative flex flex-col items-center gap-0.5 rounded-md py-0.5 px-0.5">
                            <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center ring-2 ring-emerald-400 ring-offset-1 ring-offset-white shadow-md overflow-hidden">
                                <img
                                    src="/icon-192.png"
                                    alt="BrainSync"
                                    className="w-5 h-5 rounded-md"
                                    loading="eager"
                                />
                            </div>
                            <div className="text-[5px] font-bold text-emerald-700 leading-tight">BrainSync</div>
                        </div>
                        {/* Adobe Acrobat - credible filler */}
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-7 h-7 rounded-xl bg-red-600 flex items-center justify-center text-white text-[8px] font-bold">A</div>
                            <div className="text-[5px] text-zinc-700 leading-tight">PDF</div>
                        </div>
                        {/* Claude - credible filler */}
                        <div className="flex flex-col items-center gap-0.5">
                            <div className="w-7 h-7 rounded-xl bg-orange-500 flex items-center justify-center text-white text-[10px]">✸</div>
                            <div className="text-[5px] text-zinc-700 leading-tight">Claude</div>
                        </div>
                    </div>
                </div>

                {/* Saved confirmation toast - slides down from the top
                    once the BrainSync row has been "tapped". Positioned
                    below the Dynamic Island so it doesn't get hidden by
                    the device chrome. */}
                <div className="bs-anim bs-toast absolute top-10 left-1/2 z-30 bg-emerald-600 text-white text-[9px] font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1 whitespace-nowrap">
                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                    Saved to BrainSync
                </div>
                </div>
            </div>
        </div>
    );
}

// StepBanner - the in-phone tutorial chip. Each phase of the demo
// mounts its own copy with a different keyframe class so only one is
// visible at a time. Glass background + emerald accent gives a
// premium "tutorial overlay" feel that matches BrainSync's brand.
// Sits dead-center on the phone screen and is sized large enough to
// read at the modal's natural display size without zooming.
function StepBanner({ className, n, label, tone }) {
    const dotClasses = tone === 'success'
        ? 'bg-emerald-500 text-white'
        : 'bg-emerald-100 text-emerald-700';
    return (
        <div
            className={`${className} absolute inset-x-0 flex items-center gap-2 bg-white/95 backdrop-blur-md rounded-2xl px-3 py-2 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.35)] ring-1 ring-black/5`}
            style={{ willChange: 'transform, opacity' }}
        >
            <span className={`flex-shrink-0 w-7 h-7 rounded-full ${dotClasses} text-sm font-bold flex items-center justify-center shadow-sm`}>
                {n}
            </span>
            <span className="text-[13px] font-semibold text-zinc-800 tracking-tight leading-tight">
                {label}
            </span>
        </div>
    );
}

// ── Tooltip Steps (reference data-testid targets) ──

function WelcomeSlides({ onComplete }) {
    const [current, setCurrent] = useState(0);

    const next = () => {
        if (current < SLIDES.length - 1) setCurrent(current + 1);
        else onComplete();
    };

    const prev = () => {
        if (current > 0) setCurrent(current - 1);
    };

    const slide = SLIDES[current];
    const Icon = slide.icon;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="onboarding-slides">
            <div className="relative w-full max-w-md mx-4 bg-background rounded-3xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.4)] overflow-hidden ring-1 ring-black/5">
                {/* Skip button */}
                <button
                    onClick={onComplete}
                    className="absolute top-4 right-4 z-30 text-xs text-white/70 hover:text-white transition-colors px-2.5 py-1 rounded-full bg-white/10 backdrop-blur-sm"
                    data-testid="onboarding-skip"
                >
                    Skip
                </button>

                {/* Hero area - gradient header that holds either the
                    plain icon header or the animated share demo. The
                    share-demo slide gets extra layered depth (grain +
                    inner shadow) since it's the visual centrepiece of
                    the onboarding. */}
                <div className={`relative bg-gradient-to-br ${slide.color} px-6 pt-8 pb-8 flex flex-col items-center text-white overflow-hidden`}>
                    {/* Subtle grain texture - makes the dark gradient
                        feel like a finished surface rather than a flat
                        CSS wash. Uses a data-URI SVG so we don't have
                        to ship an extra asset. */}
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
                        style={{
                            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
                        }}
                    />
                    {/* Inner vignette - frames the content slightly */}
                    <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_rgba(0,0,0,0.4)]" />

                    {/* Hero element */}
                    <div className="relative z-10 w-full flex flex-col items-center">
                        {slide.kind === 'share-demo' ? (
                            <ShareToBrainSyncDemo />
                        ) : slide.kind === 'welcome-orbit' ? (
                            <BrainSyncOrbit />
                        ) : (
                            <div className="w-20 h-20 rounded-3xl bg-white/15 flex items-center justify-center mb-6 backdrop-blur-md ring-1 ring-white/20 shadow-lg">
                                <Icon className="w-9 h-9" />
                            </div>
                        )}
                        <h2
                            className="font-heading text-2xl font-bold text-center tracking-tight"
                            style={{ lineHeight: '1.15' }}
                        >
                            {slide.title}
                        </h2>
                        <p className="mt-1.5 text-sm text-emerald-200/90 font-medium tracking-wide">
                            {slide.subtitle}
                        </p>
                    </div>
                </div>

                {/* Body */}
                <div className="px-7 py-6">
                    <p className="text-[13px] text-zinc-600 text-center leading-relaxed">{slide.description}</p>
                </div>

                {/* Footer */}
                <div className="px-7 pb-6 flex items-center justify-between">
                    {/* Dots */}
                    <div className="flex gap-1.5">
                        {SLIDES.map((_, i) => (
                            <div
                                key={i}
                                className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-6 bg-emerald-700' : 'w-1.5 bg-zinc-200'}`}
                            />
                        ))}
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center gap-2">
                        {current > 0 && (
                            <Button variant="ghost" size="sm" onClick={prev} className="h-9 w-9 p-0 rounded-full">
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                        )}
                        <Button onClick={next} size="sm" className="rounded-full px-5 gap-1.5 bg-emerald-700 hover:bg-emerald-800 shadow-md" data-testid="onboarding-next">
                            {current === SLIDES.length - 1 ? 'Get Started' : 'Next'}
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TooltipWalkthrough({ onComplete }) {
    const [step, setStep] = useState(0);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [spotlightStyle, setSpotlightStyle] = useState({});
    const tooltipRef = useRef(null);

    const currentStep = TOOLTIP_STEPS[step];

    const positionTooltip = useCallback(() => {
        if (!currentStep) return;
        const el = document.querySelector(`[data-testid="${currentStep.target}"]`);
        if (!el) {
            // Element not found (might be hidden on mobile), skip step
            if (step < TOOLTIP_STEPS.length - 1) setStep(s => s + 1);
            else onComplete();
            return;
        }

        const rect = el.getBoundingClientRect();
        const pad = 8;

        // Spotlight
        setSpotlightStyle({
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
        });

        // Tooltip positioning
        const tooltipWidth = 280;
        let top, left;

        if (currentStep.position === 'bottom') {
            top = rect.bottom + 12;
            left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
        } else if (currentStep.position === 'top-left') {
            top = rect.top - 140;
            left = Math.max(16, rect.left - tooltipWidth + rect.width);
        } else {
            top = rect.bottom + 12;
            left = Math.max(16, rect.left);
        }

        setTooltipStyle({ top, left, width: tooltipWidth });
    }, [currentStep, step, onComplete]);

    useEffect(() => {
        positionTooltip();
        window.addEventListener('resize', positionTooltip);
        return () => window.removeEventListener('resize', positionTooltip);
    }, [positionTooltip]);

    if (!currentStep) return null;

    const next = () => {
        if (step < TOOLTIP_STEPS.length - 1) setStep(step + 1);
        else onComplete();
    };

    return (
        <div className="fixed inset-0 z-[60]" data-testid="onboarding-walkthrough">
            {/* Overlay with cutout */}
            <div className="absolute inset-0">
                {/* Dark overlay */}
                <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
                    <defs>
                        <mask id="spotlight-mask">
                            <rect x="0" y="0" width="100%" height="100%" fill="white" />
                            <rect
                                x={spotlightStyle.left}
                                y={spotlightStyle.top}
                                width={spotlightStyle.width}
                                height={spotlightStyle.height}
                                rx="12"
                                fill="black"
                            />
                        </mask>
                    </defs>
                    <rect
                        x="0" y="0"
                        width="100%" height="100%"
                        fill="rgba(0,0,0,0.55)"
                        mask="url(#spotlight-mask)"
                    />
                </svg>

                {/* Spotlight ring */}
                <div
                    className="absolute rounded-xl ring-2 ring-primary/60 ring-offset-2 transition-all duration-300"
                    style={{
                        top: spotlightStyle.top,
                        left: spotlightStyle.left,
                        width: spotlightStyle.width,
                        height: spotlightStyle.height,
                        pointerEvents: 'none',
                    }}
                />
            </div>

            {/* Click-catcher to dismiss */}
            <div className="absolute inset-0" onClick={next} />

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="absolute bg-background rounded-xl border border-border shadow-2xl p-4 z-10"
                style={tooltipStyle}
                onClick={(e) => e.stopPropagation()}
                data-testid="onboarding-tooltip"
            >
                <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-bold text-foreground" style={{ lineHeight: '1.2' }}>{currentStep.title}</h3>
                    <button
                        onClick={onComplete}
                        className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-secondary transition-colors flex-shrink-0 ml-2"
                        data-testid="onboarding-tooltip-close"
                    >
                        <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{currentStep.description}</p>
                <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground font-medium">
                        {step + 1} of {TOOLTIP_STEPS.length}
                    </span>
                    <Button onClick={next} size="sm" className="h-7 rounded-full px-3 text-xs gap-1" data-testid="onboarding-tooltip-next">
                        {step === TOOLTIP_STEPS.length - 1 ? 'Done' : 'Next'}
                        {step < TOOLTIP_STEPS.length - 1 && <ChevronRight className="w-3 h-3" />}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function OnboardingOverlay() {
    const { user, authBootstrapReady } = useAuth();
    const [phase, setPhase] = useState('slides'); // 'slides' | 'walkthrough' | 'done'
    const [show, setShow] = useState(false);
    const suppressedFiredRef = useRef(false);

    // Fix B (Aug 2026): users who signed up via the anonymous try-it
    // flow have already seen the product live. The suppression MUST
    // gate at the render level — never mount-then-dismiss — so the
    // carousel never flashes. Guarded by `flag_claim_path_onboarding`
    // (default OFF), so populations without the flag keep exactly
    // today's behavior.
    const suppress = FLAGS.CLAIM_PATH_ONBOARDING && !!user?.onboarded_via_anon_save;

    useEffect(() => {
        // Fix B follow-up (Aug 2026): do NOT start the 800ms reveal
        // timer until auth bootstrap has terminated (which, on the
        // claim path, only happens once the /try/claim POST has
        // resolved and `onboarded_via_anon_save` has been merged into
        // the committed user). Without this gate the timer starts
        // during the ~200-500ms window where `user.onboarded_via_anon_save`
        // is transiently false — and the overlay mounts before the
        // suppression flag lands.
        if (!authBootstrapReady) return;
        if (suppress) return;
        if (localStorage.getItem(ONBOARDING_KEY) === '1') return;
        // Small delay to let dashboard render first
        const timer = setTimeout(() => setShow(true), 800);
        return () => clearTimeout(timer);
    }, [suppress, authBootstrapReady]);

    // Fire the analytics event exactly once per mount for a suppressed
    // user (Fix B). Gated on `authBootstrapReady` so we don't count a
    // transient pre-claim suppress=false → true flip as two events.
    useEffect(() => {
        if (!authBootstrapReady) return;
        if (!suppress || suppressedFiredRef.current) return;
        suppressedFiredRef.current = true;
        try { track('onboarding_suppressed', { reason: 'anon_claim' }); } catch (_) { /* best-effort */ }
    }, [suppress, authBootstrapReady]);

    const completeSlides = useCallback(() => setPhase('walkthrough'), []);

    const completeOnboarding = useCallback(() => {
        setPhase('done');
        setShow(false);
        localStorage.setItem(ONBOARDING_KEY, '1');
    }, []);

    if (suppress) return null;
    if (!show || phase === 'done') return null;

    if (phase === 'slides') {
        return <WelcomeSlides onComplete={completeSlides} />;
    }

    return <TooltipWalkthrough onComplete={completeOnboarding} />;
}
